/**
 * Reusable Tailwind class strings for cross-surface primitives.
 *
 * Pre-Track 0.2 of UNIFIED_FRONTEND_ROADMAP.md replaced the `pp-*`
 * BEM classes that lived in `src/styles/shared.css` and the dialog /
 * field / button helpers in `src/styles/layout.css`. These strings are
 * the literal Tailwind translation of those rules, exported as
 * constants so consumers can apply them via `cn()` instead of
 * duplicating ~10 utilities at every callsite.
 *
 * Compose with `cn()` when you need to add or override utilities:
 *   <button className={cn(iconBtn, 'text-error')}>…</button>
 */

/** Inline-flex 28×28 icon button, transparent → surface-1 on hover. */
export const iconBtn =
  'inline-flex items-center justify-center w-7 h-7 rounded-sm text-text-tertiary bg-transparent enabled:hover:text-text-primary enabled:hover:bg-surface-1';

/** Smaller 22×22 icon button (used inside MatterGroup row). */
export const iconBtnSmall =
  'inline-flex items-center justify-center w-[22px] h-[22px] rounded-sm text-text-tertiary bg-transparent enabled:hover:text-text-primary enabled:hover:bg-surface-1';

/** Standard button shell — primary / ghost variants apply on top. */
export const btnBase =
  'font-ui text-sm px-4 py-2 rounded-sm border border-border disabled:opacity-50 disabled:cursor-not-allowed';

export const btnPrimary =
  'bg-navy-700 text-surface-white border-navy-700 enabled:hover:bg-navy-500';

export const btnGhost =
  'bg-transparent text-text-secondary enabled:hover:bg-surface-1 enabled:hover:text-text-primary';

/** Modal backdrop — covers the viewport, centers the dialog. */
export const dialogBackdrop =
  'fixed inset-0 flex items-center justify-center z-[1000] p-4 bg-[rgba(26,22,18,0.36)]';

/** Modal frame — paper card, max 420px, drop shadow. */
export const dialog =
  'w-full max-w-[420px] max-h-[calc(100dvh-2rem)] min-w-0 flex flex-col bg-surface-white border border-border rounded-md shadow-[0_20px_60px_rgba(26,22,18,0.18)]';

export const dialogHeader =
  'flex items-center justify-between gap-3 min-w-0 px-4 py-3 border-b border-border';

export const dialogTitle = 'min-w-0 font-prose text-md m-0 text-text-primary';

export const dialogBody = 'min-h-0 overflow-y-auto flex flex-col gap-3 p-4';

export const dialogActions =
  'flex justify-end gap-2 mt-2 max-mobile:flex-col-reverse max-mobile:[&>button]:w-full';

/** Form field column. */
export const field = 'flex flex-col gap-1';

export const fieldLabel =
  'font-ui text-xs uppercase tracking-[0.06em] text-text-tertiary';

export const fieldInput =
  'font-ui text-sm text-text-primary bg-surface-white border border-border rounded-md px-3 py-2 transition-colors focus:outline-none focus:border-navy-500';

export const fieldError = 'm-0 text-error text-sm';

/** Generic skeleton block — shimmer animation keyed off theme.css. */
export const skeleton =
  'rounded-md bg-[length:200%_100%] animate-shimmer [background-image:linear-gradient(90deg,var(--pp-surface-1)_0%,var(--pp-surface-white)_50%,var(--pp-surface-1)_100%)]';

/** Page-shell primitives shared across the four feature pages
 * (ClaimCharts, MatterDetail, TabularReviews, Workflows). They were
 * `pp-cc-*` BEM classes in `src/styles/claim-chart.css` before
 * Pre-Track 0.4 — extracted here so the four pages and the matter /
 * workflow surfaces don't duplicate the same ~10 utilities at every
 * use site. */
export const appMain =
  'flex-1 min-w-0 min-h-0 flex flex-col bg-surface-1 overflow-hidden';

export const pageScroll =
  'flex-1 min-h-0 min-w-0 flex flex-col py-5 px-6 gap-5 overflow-y-auto max-mobile:px-3 max-mobile:py-4';

/** Detail-view variant: same scrollable column, but the inner body is
 * expected to take ownership of its own overflow (e.g. a sticky table). */
export const pageScrollDetail =
  'flex-1 min-h-0 min-w-0 flex flex-col py-5 px-6 gap-5 overflow-hidden max-mobile:px-3 max-mobile:py-4';

export const pageTop =
  'flex items-center justify-between gap-4 flex-wrap';

export const pageTopDetail =
  'flex items-start justify-between gap-3 flex-wrap';

export const pageTitle =
  'font-prose text-lg font-semibold text-text-primary m-0';

export const pageTitleBlock = 'flex-1 min-w-0';

export const pageMeta =
  'font-ui text-xs text-text-tertiary flex gap-2 flex-wrap';

export const pageActions = 'flex gap-2 flex-wrap';

export const pageHint = 'text-text-tertiary font-ui text-sm m-0';

export const pageCrumb =
  'font-ui text-xs text-text-tertiary ml-3 uppercase tracking-[0.04em] max-mobile:hidden';

/** List-card primitives — used by the ClaimCharts and TabularReviews list
 * views and the MatterReviewsSection grid. */
export const cardGrid =
  'list-none m-0 p-0 grid grid-cols-[repeat(auto-fill,minmax(min(280px,100%),1fr))] gap-3';

export const cardItem =
  'bg-surface-white border border-border rounded-md transition-colors hover:border-border-emphasis hover:shadow-sm';

export const cardLink =
  'flex flex-col gap-1 p-4 text-inherit no-underline';

export const cardName =
  'font-prose text-base font-semibold text-text-primary';

export const cardMeta =
  'flex gap-2 flex-wrap font-ui text-xs text-text-tertiary';

/** Empty state — papyrus-card variant used across the feature surfaces. */
export const emptyState =
  'flex flex-col items-center justify-center text-center py-8 px-5 gap-3 bg-surface-white border border-dashed border-border rounded-lg mx-auto max-w-[520px] w-full';

export const emptyTitle =
  'font-prose text-md font-semibold text-text-primary m-0';

export const emptyBody =
  'text-text-tertiary font-ui text-sm m-0 max-w-[460px]';

export const emptyActions = 'flex gap-2 flex-wrap justify-center';

/** Sidebar nav link — used by the three section shortcuts in the
 * Sidebar component. Was `pp-sidebar__nav-link` in `claim-chart.css`. */
export const sidebarNavLink =
  'flex items-center gap-2 px-3 py-2 font-ui text-sm text-text-secondary bg-transparent rounded-md cursor-pointer text-left mx-2 mb-2 transition-colors hover:bg-surface-2 hover:text-text-primary data-active:bg-navy-100 data-active:text-navy-700 data-active:font-medium';

/** App-shell + header primitives shared by the four feature pages
 * (ClaimCharts, MatterDetail, TabularReviews, Workflows) and the chat
 * surface. Pre-Track 0.4 duplicated these constants 4× across the page
 * files; Pre-Track 0.5 collapsed them here so the page chrome is one
 * source of truth. */
// `pp-grain` is the SPEC_11 static texture overlay — active on the ink
// theme only (see theme.css), inert on light/dark.
export const appShell =
  'flex flex-col h-screen h-[100dvh] min-h-0 min-w-0 overflow-hidden bg-surface-0 text-text-primary font-prose pp-grain';

export const appBody = 'flex-1 flex min-h-0 min-w-0 relative overflow-hidden';

export const headerShell =
  'flex-shrink-0 h-12 min-h-12 min-w-0 flex items-center justify-between gap-3 px-6 border-b border-border bg-surface-white max-mobile:px-3';

export const headerSide = 'flex items-center gap-3 min-w-0';

export const headerLogo =
  'font-chrome text-sm font-semibold tracking-[0.05em] uppercase text-text-primary min-w-0 max-mobile:[&>span>span:last-child]:hidden';

export const headerToggle =
  'flex items-center justify-center w-8 h-8 rounded-md text-text-secondary bg-transparent hover:bg-surface-1 hover:text-text-primary';

export const headerThemeBtn =
  'inline-flex items-center justify-center w-7 h-7 rounded-md text-text-secondary bg-transparent transition-colors hover:bg-surface-1 hover:text-text-primary';

export const headerEmail =
  'font-ui text-xs text-text-tertiary max-mobile:hidden';

export const headerSignOut =
  'font-ui text-xs text-text-secondary px-2 py-1 rounded-md bg-transparent hover:bg-surface-1 hover:text-text-primary';

/** Mobile sidebar backdrop — clickable scrim shown only when the sidebar
 * is toggled open on viewports ≤ 1024px. */
export const mobileBackdrop =
  'hidden max-tablet:data-shown:block max-tablet:data-shown:absolute max-tablet:data-shown:inset-0 max-tablet:data-shown:z-20 max-tablet:data-shown:bg-text-primary max-tablet:data-shown:opacity-30';
